import type { ErpModule } from '@/api/platform.api';

export interface TourStepDefinition {
  id: string;
  title: string;
  description: string;
  actionTo: string;
  actionLabel: string;
  /** Route prefixes that count as visiting this step (for report / pipeline style steps). */
  visitPrefixes?: string[];
}

export const TOUR_STEPS: Partial<Record<ErpModule, TourStepDefinition[]>> = {
  POS: [
    {
      id: 'store',
      title: 'Create a store',
      description: 'Set up your first retail location.',
      actionTo: '/pos/stores',
      actionLabel: 'Open stores',
    },
    {
      id: 'product',
      title: 'Add products',
      description: 'Build your catalog with prices and tax rules.',
      actionTo: '/pos/products',
      actionLabel: 'Add product',
    },
    {
      id: 'customer',
      title: 'Add customers',
      description: 'Register customers for loyalty and invoicing.',
      actionTo: '/pos/customers',
      actionLabel: 'Add customer',
    },
    {
      id: 'sale',
      title: 'Record a sale',
      description: 'Complete your first transaction on the POS terminal.',
      actionTo: '/pos',
      actionLabel: 'Open POS',
    },
    {
      id: 'reports',
      title: 'View reports',
      description: 'Track performance and sales trends.',
      actionTo: '/pos/reports',
      actionLabel: 'View reports',
      visitPrefixes: ['/pos/reports', '/pos/terminal-reports', '/pos/analytics'],
    },
  ],
  CRM: [
    {
      id: 'lead',
      title: 'Create a lead',
      description: 'Capture prospects from the Leads workspace.',
      actionTo: '/crm/leads',
      actionLabel: 'Open leads',
    },
    {
      id: 'convert',
      title: 'Convert lead',
      description: 'Qualify and convert a lead to a contact or deal.',
      actionTo: '/crm/leads',
      actionLabel: 'Convert lead',
    },
    {
      id: 'deal',
      title: 'Create a deal',
      description: 'Track opportunities in your pipeline.',
      actionTo: '/crm/deals',
      actionLabel: 'Create deal',
    },
    {
      id: 'pipeline',
      title: 'Track pipeline',
      description: 'Move deals through stages on the board.',
      actionTo: '/crm/pipelines',
      actionLabel: 'Open pipeline',
      visitPrefixes: ['/crm/pipelines', '/crm/deals'],
    },
  ],
  HR: [
    {
      id: 'employee',
      title: 'Add employee',
      description: 'Create employee profiles under Employees.',
      actionTo: '/hr/employees',
      actionLabel: 'Add employee',
    },
    {
      id: 'attendance',
      title: 'Configure attendance',
      description: 'Set policies and record attendance.',
      actionTo: '/hr/attendance',
      actionLabel: 'Set up attendance',
      visitPrefixes: ['/hr/attendance', '/hr/shifts', '/hr/leave-policies'],
    },
    {
      id: 'payroll',
      title: 'Run payroll',
      description: 'Process payroll when your cycle is ready.',
      actionTo: '/hr/payroll',
      actionLabel: 'Open payroll',
      visitPrefixes: ['/hr/payroll'],
    },
  ],
};

export function matchesVisitPrefix(pathname: string, prefixes?: string[]): boolean {
  if (!prefixes?.length) return false;
  return prefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}
